import { AttributesMixin, Model, define, type } from "@type-r/models";
import { matchString } from "api/common";
import { asTimelessDate } from "lqx-react/lib";
import { AssetChange } from "./assetChange";
import { AssetData, AssetType as AssetObject } from "./assetData";
import { AssetEvent } from "./assetEvent";
import { AssetType } from "./assetType";
import { CountsModel } from "./countsModel";
import { restfulIO } from "./endpoint";

export interface CustomerReferenceData extends AttributesMixin<typeof CustomerReferenceData> { }

@define
export class CustomerReferenceData extends AssetData {

    static attributes = {
        ...AssetData.attributes,
        assetType: type(AssetObject).value({ id: 12, name: 'Customer Reference Data' }),
        uniqueKey: String,
        customerNumber: String,
        customerName: String,
        countryCode: String,
        effectiveDate: type(Date).null,
        companyCode: String,
        globalParentCode: String,
        address1: String,
        address2: String,
        address3: String,
        city: String,
        stateProvince: String,
        regionCode: String,
        phoneNumber: String,
        faxNumber: String,
        email: String,
        affiliateFlag: String,
        governmentFlag: String,
        assignabilityFlag: String,
        creditCustomerFlag: String,
        bankruptcyFlag: String,
        customerInsuranceLimit: Number,
        customerCreditLimit: Number,
        customerVATNumber: String,
        notifiedFlag: String,
        contactName: String,
        dunsNumber: String,
        region: String,
    }

    onLoaded() { }

    get isDigitized() {
        return this.state.name == 'Digitized';
    }
}

export interface ManualCustomerReferenceData extends AttributesMixin<typeof ManualCustomerReferenceData> { }

@define
export class ManualCustomerReferenceData extends Model {
    static endpoint = restfulIO('api/v1/ingest/customerreferencesdata');

    static attributes = {
        id: String,
        status: String,
        ...CustomerReferenceData.attributes
    }
}

export interface CustomerReferenceDataChange extends AttributesMixin<typeof CustomerReferenceDataChange> { }

@define
export class CustomerReferenceDataChange extends AssetChange {
    static endpoint = restfulIO('api/v1/ingest/customerreferencesdata/assets');

    static attributes = {
        ...AssetChange.attributes,
        updateAsset: CustomerReferenceData,
        assetType: type(Number).value(AssetType.customerReferenceData),
        assetEvents: type([AssetEvent]).value([]).dontSave
    };

    getAssetType() {
        return AssetType.customerReferenceData;
    }

    getAssetName() {
        return 'Customer Reference Data';
    }

    get effectiveDate(): Date {
        const value = this.getValue("effectiveDate");
        return value ? new Date(value) : null;
    }

    get customerNumber(): String {
        return this.getValue("customerNumber");
    }

    get customerName(): String {
        return this.getValue("customerName");
    }

    get countryCode(): String {
        return this.getValue("countryCode");
    }

    filter(filter) {
        return (matchString(asTimelessDate(this.effectiveDate), filter.search) ||
            matchString(this.customerNumber.toString(), filter.search) ||
            matchString(this.customerName.toString(), filter.search) ||
            matchString(this.countryCode.toString(), filter.search) ||
            matchString(this.state.name, filter.search)) &&
            this.hasReceivedAssetEventsFor(filter.eventType);
    }
}

export interface CustomerReferenceDataObject extends AttributesMixin<typeof CustomerReferenceDataObject> { }
@define
export class CustomerReferenceDataObject extends Model {
    static endpoint = restfulIO('api/v1/ingest/customerreferencesdata/assets');

    static attributes = {
        count: Number,
        items: [CustomerReferenceDataChange]
    };
}

export interface CustomerReferenceDataCount extends AttributesMixin<typeof CustomerReferenceDataCount> { }
@define
export class CustomerReferenceDataCount extends CountsModel {
    static endpoint = restfulIO('api/v1/ingest/customerreferencesdata/counts');

    static attributes = {
        ...CountsModel.attributes
    };
}